// Utility functions for PANInfoJoint component
import moment from "moment";
import { errorCode, errorMsg } from "../../../ErrorCode/ErrorCode";

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

export const PAN_VALIDATION_KEY_LIST = {
    PanStatus: "PanStatusMsg",
    Name: "NameMsg",
    Dob: "DobMsg",
    SeedingStatus: "SeedingStatusMsg",
} as const;

export const SUBTYPE_MAP: Record<string, number> = {
    LLP: 24,
    BodyCorp: 44,
    HUF: 3,
    Partnership: 2,
    Trust: 5
};

export const SUBTYPE_DOCUMENT_ID: Record<string, number> = {
    LLP: 659,
    BodyCorp: 657,
    HUF: 655,
    Partnership: 661,
    Trust: 690,
};

export const MIN_AGE = 18;
export const MAX_AGE = 100;
export const SENIOR_CITIZEN_AGE = 60;

export const convertDate = (dateString: string): string => {
    const [day, month, year] = dateString.split("/");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}-${day.padStart(2, "0")}-${year}`;
};

export const isMinor = (dob: Date | null): boolean => {
    if (!dob) return false;
    const today = new Date();
    const birthDate = new Date(dob);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        return age - 1 < MIN_AGE;
    }
    return age < MIN_AGE;
};

export const checkPanValidationMsgs = (response: Record<string, string>): string => {
    return Object.keys(PAN_VALIDATION_KEY_LIST)
        .filter((key) => response[key] === "N")
        .map((key) => response[PAN_VALIDATION_KEY_LIST[key as keyof typeof PAN_VALIDATION_KEY_LIST]])
        .join("\n");
};

export const buildFullName = (
    firstName?: string,
    middleName?: string,
    lastName?: string
): string => {
    const first = firstName || "";
    const middle = middleName || "";
    const last = lastName || "";

    if (!first && !middle && !last) return "";
    if (!first && last) return `${last} ${middle}`.trim();
    if (!middle) return `${first} ${last}`.trim();
    return `${first} ${middle} ${last}`.trim();
};

export const validateNameField = (
    value: string | undefined,
    eventValue: string,
    isRequired: boolean = false
): string | undefined => {
    if (isRequired && !eventValue) {
        return errorMsg(errorCode.FE57);
    }
    if (value && value.slice(-1) === " ") {
        return errorMsg(errorCode.FE55);
    }
    if (eventValue && eventValue.length > 0 && !eventValue.replace(/\s/g, "").length) {
        return errorMsg(errorCode.FE55);
    }
    return undefined;
};

export const formatPANName = (
    firstName?: string,
    middleName?: string,
    lastName?: string
): { PANName: string; firstname: string; middlename: string; lastname: string } => {
    let PANFirstName = firstName || "";
    let PANMiddleName = middleName || "";
    let PANlastName = lastName || "";

    if (PANFirstName === "" && PANlastName !== "") {
        PANFirstName = PANlastName;
        PANlastName = "";
        if (PANMiddleName !== "") {
            return {
                PANName: `${PANFirstName} ${PANMiddleName}`,
                firstname: PANFirstName,
                middlename: PANMiddleName,
                lastname: "",
            };
        }
        return {
            PANName: PANFirstName,
            firstname: PANFirstName,
            middlename: PANMiddleName,
            lastname: "",
        };
    }

    if (PANFirstName === "" && PANMiddleName === "" && PANlastName === "") {
        return {
            PANName: "",
            firstname: "",
            middlename: "",
            lastname: "",
        };
    }

    if (PANMiddleName === "") {
        return {
            PANName: `${PANFirstName} ${PANlastName}`,
            firstname: PANFirstName,
            middlename: "",
            lastname: PANlastName,
        };
    }

    return {
        PANName: `${PANFirstName} ${PANMiddleName} ${PANlastName}`,
        firstname: PANFirstName,
        middlename: PANMiddleName,
        lastname: PANlastName,
    };
};

export const filterNavigationBar = (
    leftNavBar: any[],
    urlsToRemove: string[],
    urlToAdd?: any
): any[] => {
    const filtered = [...leftNavBar];
    urlsToRemove.forEach((url) => {
        const index = filtered.findIndex((item) => item?.navUrl === url);
        if (index > 0) {
            filtered.splice(index, 1);
        }
    });
    if (urlToAdd && !filtered.includes(urlToAdd)) {
        filtered.push(urlToAdd);
    }
    return filtered;
};

export const updateClientPartialDetails = (
    existing: any,
    updates: any
): any => {
    return {
        ...existing,
        ...updates,
    };
};

